import React, { useMemo, useState } from "react";
import { Sparkles, Calculator, SlidersHorizontal } from "lucide-react";
import Slider from "../../components/clients/Slider";
import ScoreBadge from "../../components/clients/ScoreBadge";
import { clientsSeed } from "../../data/clients";
import {
  defaultWeights,
  normalizeWeights,
  computeScore,
  scoreToColor,
  scoreTooltip,
  computeClientMetrics,
} from "../../utils/scoring";

export default function ClientScoring() {
  const [weights, setWeights] = useState(defaultWeights);

  // Simulation inputs (0..100)
  const [sim, setSim] = useState({
    confirmationRate: 82,
    cancellationRate: 12,
    returnRate: 6,
    responseRate: 88,
  });

  const normalized = useMemo(() => normalizeWeights(weights), [weights]);

  const simScore = useMemo(() => {
    const metrics = {
      confirmationRate: sim.confirmationRate,
      cancellationRate: sim.cancellationRate,
      returnRate: sim.returnRate,
      responseRate: sim.responseRate,
    };
    return computeScore(metrics, normalized);
  }, [sim, normalized]);

  const simColor = useMemo(() => scoreToColor(simScore, { blocked: false, manualRiskFlag: false }), [simScore]);
  const simTooltip = useMemo(() => scoreTooltip(sim, simColor, {}), [sim, simColor]);

  const examples = useMemo(() => {
    // show a few real clients scored with current weights
    return clientsSeed.slice(0, 3).map((c) => {
      const metrics = computeClientMetrics(c);
      const score = computeScore(metrics, normalized);
      const color = scoreToColor(score, c);
      const tooltip = scoreTooltip(metrics, color, c);
      return { ...c, metrics, trust: { score, color, tooltip } };
    });
  }, [normalized]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto p-6 space-y-5">
        <header className="flex flex-col gap-2">
          <div className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Client Scoring Intelligence</div>
          <div className="text-sm text-gray-600 dark:text-slate-400">
            Understand scoring logic, adjust weights, and simulate outcomes.
          </div>
        </header>

        <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 rounded-3xl bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-sm p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  Scoring Logic Breakdown
                </div>
                <div className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                  Score is computed from behavior rates (0–100). Cancellation/return are penalties.
                </div>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 dark:bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-700 dark:text-indigo-400">
                <SlidersHorizontal className="h-4 w-4" />
                Weights normalized to 100%
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/60 p-4">
                <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Formula (with weights)</div>
                <div className="mt-2 text-xs text-gray-700 dark:text-slate-300 leading-relaxed">
                  <div>
                    <span className="font-semibold">score</span> =
                    (confirmation_rate × {normalized.confirmation}%)
                    + ((100 − cancellation_rate) × {normalized.cancellation}%)
                    + ((100 − return_rate) × {normalized.returns}%)
                    + (response_rate × {normalized.response}%)
                  </div>
                  <div className="mt-2 text-gray-500 dark:text-slate-500">
                    Then we map: <span className="font-semibold">≥80 Green</span>, <span className="font-semibold">≥60 Blue</span>,
                    <span className="font-semibold">≥35 Red</span>, else <span className="font-semibold">Black</span>.
                    Blocked clients are always Black.
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">AI Recommendation (mock)</div>
                <div className="mt-2 text-sm text-gray-700 dark:text-slate-300">
                  • Increase <span className="font-semibold">response</span> weight if your store heavily depends on phone confirmation.<br />
                  • If returns are expensive, raise <span className="font-semibold">returns</span> weight to penalize return-heavy clients.
                </div>
                <div className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-gray-50 dark:bg-slate-800/60 px-3 py-2 text-xs text-gray-600 dark:text-slate-400">
                  <Sparkles className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  (Mock) Replace with your AI service later.
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Slider
                label="Confirmation weight"
                value={weights.confirmation}
                onChange={(v) => setWeights((w) => ({ ...w, confirmation: v }))}
              />
              <Slider
                label="Cancellation weight"
                value={weights.cancellation}
                onChange={(v) => setWeights((w) => ({ ...w, cancellation: v }))}
              />
              <Slider
                label="Return weight"
                value={weights.returns}
                onChange={(v) => setWeights((w) => ({ ...w, returns: v }))}
              />
              <Slider
                label="Response weight"
                value={weights.response}
                onChange={(v) => setWeights((w) => ({ ...w, response: v }))}
              />
            </div>

            <div className="mt-4 rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/60 p-4">
              <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Current normalized weights</div>
              <div className="mt-2 text-sm text-gray-700 dark:text-slate-300">
                Confirmation: <span className="font-semibold text-indigo-700 dark:text-indigo-400">{normalized.confirmation}%</span> •
                Cancellation: <span className="font-semibold text-indigo-700 dark:text-indigo-400"> {normalized.cancellation}%</span> •
                Returns: <span className="font-semibold text-indigo-700 dark:text-indigo-400"> {normalized.returns}%</span> •
                Response: <span className="font-semibold text-indigo-700 dark:text-indigo-400"> {normalized.response}%</span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-sm p-6">
            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Simulation Mode</div>
            <div className="text-xs text-gray-500 dark:text-slate-500 mt-1">Change behavior rates and see score update live.</div>

            <div className="mt-4 space-y-4">
              <SimInput
                label="Confirmation Rate %"
                value={sim.confirmationRate}
                onChange={(v) => setSim((s) => ({ ...s, confirmationRate: v }))}
              />
              <SimInput
                label="Cancellation Rate %"
                value={sim.cancellationRate}
                onChange={(v) => setSim((s) => ({ ...s, cancellationRate: v }))}
              />
              <SimInput
                label="Return Rate %"
                value={sim.returnRate}
                onChange={(v) => setSim((s) => ({ ...s, returnRate: v }))}
              />
              <SimInput
                label="Response Rate %"
                value={sim.responseRate}
                onChange={(v) => setSim((s) => ({ ...s, responseRate: v }))}
              />

              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/60 p-4">
                <div className="text-xs text-gray-500 dark:text-slate-500">Result</div>
                <div className="mt-2">
                  <ScoreBadge color={simColor} score={simScore} tooltip={simTooltip} />
                </div>
                <div className="mt-2 text-xs text-gray-600 dark:text-slate-400">{simTooltip}</div>
              </div>

              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 p-4">
                <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Live examples (mock clients)</div>
                <div className="mt-3 space-y-3">
                  {examples.map((c) => (
                    <div key={c.id} className="rounded-2xl bg-gray-50 dark:bg-slate-800/60 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">{c.fullName}</div>
                          <div className="text-xs text-gray-500 dark:text-slate-500">{c.city} • {c.phone}</div>
                        </div>
                        <ScoreBadge color={c.trust.color} score={c.trust.score} tooltip={c.trust.tooltip} />
                      </div>
                      <div className="mt-2 text-xs text-gray-600 dark:text-slate-400">
                        Confirm {Math.round(c.metrics.confirmationRate)}% • Cancel {Math.round(c.metrics.cancellationRate)}% •
                        Return {Math.round(c.metrics.returnRate)}% • Response {Math.round(c.metrics.responseRate)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function SimInput({ label, value, onChange }) {
  return (
    <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">{label}</div>
        <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{value}%</div>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-3 w-full accent-indigo-600"
      />
    </div>
  );
}
